import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import { portalQuizTodoModalsToBody } from "../../../../utils/quiz_results_todo_modal";
import { seemsOffline } from "../../../../utils/connectivity";
import { isPwaEnabled } from "../../../../utils/offline_gating";

const DESKTOP_MIN_WIDTH = 1024;
const STUDY_PANE_FRAME_ID = "quiz_player_study_pane";

export default class extends Controller {
  static targets = ["overlay", "toggle", "openButton", "closeButton", "banner"];

  static outlets = ["offline-navigation"];

  connect() {
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onStudyPaneFrameMissing = this.onStudyPaneFrameMissing.bind(this);
    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("keydown", this.onKeydown);
    window.addEventListener("resize", this.onResize);
    this.studyPaneFrame()?.addEventListener(
      "turbo:frame-missing",
      this.onStudyPaneFrameMissing,
    );
    if (document.getElementById("quiz-results-add-manage-todo-modal")) {
      portalQuizTodoModalsToBody();
    }
    this.syncNav();
    this.announceProgress();
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("resize", this.onResize);
    this.studyPaneFrame()?.removeEventListener(
      "turbo:frame-missing",
      this.onStudyPaneFrameMissing,
    );
  }

  toggleNav(event) {
    event.preventDefault();
    if (this.desktop()) return;

    this.lastToggle = event.currentTarget;
    if (this.open()) {
      this.closeNav();
    } else {
      this.openNav();
    }
  }

  openReport(event) {
    event.preventDefault();
    if (isPwaEnabled() && seemsOffline()) {
      if (this.hasOfflineNavigationOutlet) {
        this.offlineNavigationOutlet.showFeatureUnavailable(event.currentTarget);
      }
      return;
    }

    const modal = document.getElementById("quiz-player-report-issue");
    if (!modal) return;

    this.application
      .getControllerForElementAndIdentifier(modal, "railsui-modal")
      ?.show(event);
  }

  openReset(event) {
    event.preventDefault();
    const modal = document.getElementById("quiz-player-reset-question");
    if (!modal) return;

    this.application
      .getControllerForElementAndIdentifier(modal, "railsui-modal")
      ?.show(event);
  }

  openSaveTodo(event) {
    event.preventDefault();
    const modal = document.getElementById("quiz-player-save-quiz-todo");
    if (!modal) return;

    this.application
      .getControllerForElementAndIdentifier(modal, "railsui-modal")
      ?.show(event);
  }

  openNav() {
    this.setOpen(true);
    this.navElement()?.querySelector("button, a")?.focus();
  }

  closeNav() {
    this.setOpen(false);
    const toggle =
      this.lastToggle || (this.hasToggleTarget ? this.toggleTarget : null);
    toggle?.focus();
  }

  onDocumentClick(event) {
    const nav = this.navElement();
    if (!nav) return;
    if (!this.open() || this.desktop()) return;
    if (nav.contains(event.target)) return;
    if (this.toggleTargets.some((toggle) => toggle.contains(event.target)))
      return;
    if (
      this.hasCloseButtonTarget &&
      this.closeButtonTarget.contains(event.target)
    )
      return;
    if (
      event.target instanceof Element &&
      (event.target.closest("#quiz-player-report-issue") ||
        event.target.closest("#quiz-player-reset-question") ||
        event.target.closest("#quiz-player-save-quiz-todo"))
    )
      return;

    this.closeNav();
  }

  onKeydown(event) {
    if (!this.navElement()) return;
    if (!this.open() || this.desktop()) return;

    if (event.key === "Escape") {
      if (this.reportDialogOpen()) return;
      this.closeNav();
      return;
    }

    if (event.key === "Tab") this.trapFocus(event);
  }

  onResize() {
    if (this.desktop() && this.open()) this.setOpen(false);
    this.syncNav();
  }

  announceProgress() {
    const liveRegion = document.getElementById("progressbar-live");
    if (!liveRegion) return;

    const text = liveRegion.textContent;
    liveRegion.textContent = "";
    window.setTimeout(() => {
      liveRegion.textContent = text;
    }, 100);
  }

  onStudyPaneFrameLoad(event) {
    if (event.target.id !== STUDY_PANE_FRAME_ID) return;

    this.setOpen(false);
    this.scrollStudyPaneToTop();
    this.announceProgress();
    this.questionElement()?.focus({ preventScroll: true });
  }

  onStudyPaneFrameMissing(event) {
    if (event.target.id !== STUDY_PANE_FRAME_ID) return;

    event.preventDefault();
    const url = event.detail?.response?.url;
    if (url) {
      Turbo.visit(url, { action: "replace" });
    } else {
      window.location.reload();
    }
  }

  scrollStudyPaneToTop() {
    const scrollRoot =
      document.getElementById("main-content") || document.documentElement;
    scrollRoot.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }

  desktop() {
    return window.innerWidth >= DESKTOP_MIN_WIDTH;
  }

  open() {
    const nav = this.navElement();
    if (!nav) return false;

    return nav.dataset.open === "true";
  }

  setOpen(open) {
    const nav = this.navElement();
    if (!nav) return;

    const value = open ? "true" : "false";
    nav.dataset.open = value;
    if (this.hasOverlayTarget) this.overlayTarget.dataset.open = value;
    this.toggleTargets.forEach((toggle) => {
      toggle.setAttribute("aria-expanded", value);
    });
    if (this.hasOpenButtonTarget) this.openButtonTarget.hidden = open;
    if (this.hasCloseButtonTarget) {
      this.closeButtonTarget.hidden = !open || this.desktop();
    }
    this.syncDialog(open);
    this.syncNav();
  }

  syncNav() {
    const nav = this.navElement();
    if (!nav) return;

    const drawerOpen = !this.desktop() && this.open();
    const showNav = this.desktop() || this.open();
    nav.inert = !showNav;
    const question = this.questionElement();
    if (question) question.inert = drawerOpen;
    if (this.hasBannerTarget) this.bannerTarget.inert = drawerOpen;
    const summary = this.summaryElement();
    if (summary) summary.inert = drawerOpen;
  }

  syncDialog(open) {
    const nav = this.navElement();
    if (!nav) return;

    const dialog = open && !this.desktop();
    if (dialog) {
      nav.setAttribute("role", "dialog");
      nav.setAttribute("aria-modal", "true");
      nav.setAttribute("aria-labelledby", "quiz-player-sheet-title");
      return;
    }

    nav.removeAttribute("role");
    nav.removeAttribute("aria-modal");
    nav.removeAttribute("aria-labelledby");
  }

  reportDialogOpen() {
    return (
      this.dialogVisible("quiz-player-report-issue-dialog") ||
      this.dialogVisible("quiz-player-reset-question-dialog") ||
      this.dialogVisible("quiz-player-save-quiz-todo-dialog")
    );
  }

  dialogVisible(id) {
    const dialog = document.getElementById(id);
    return Boolean(dialog && !dialog.classList.contains("hidden"));
  }

  trapFocus(event) {
    const focusable = this.focusableNavItems();
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (focusable.length === 1 || (event.shiftKey && active === first)) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  focusableNavItems() {
    const nav = this.navElement();
    if (!nav) return [];

    const items = [...nav.querySelectorAll("button, a")].filter((element) =>
      this.focusable(element),
    );
    if (this.hasCloseButtonTarget && this.focusable(this.closeButtonTarget)) {
      items.push(this.closeButtonTarget);
    }
    return items;
  }

  focusable(element) {
    return !element.disabled && !element.hidden && element.tabIndex !== -1;
  }

  studyPaneFrame() {
    return document.getElementById(STUDY_PANE_FRAME_ID);
  }

  navElement() {
    return this.element.querySelector("#quiz-player-nav");
  }

  questionElement() {
    return this.element.querySelector("#quiz-player-question");
  }

  summaryElement() {
    return this.element.querySelector("#quiz-player-summary");
  }
}
